import React, { useEffect, useMemo, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { RevealText } from './common/RevealText';
import ScrollParagraph from './common/ScrollParagraph';
import AnimatedCounter from './common/AnimatedCounter';
import Container from './common/Container';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllProjects } from '../redux/slice/project.slice';
import { ArrowUpRight } from 'lucide-react';

const About = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { projects } = useSelector((state) => state.project);
    const containerRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

    useEffect(() => {
        dispatch(getAllProjects());
    }, [dispatch]);

    const stats = useMemo(() => {
        const totalProjects = projects?.length || 0;
        const totalArea = projects?.reduce((acc, curr) => acc + (parseFloat(curr.projectArea) || 0), 0) || 0;

        // Calculate starting year
        let startingYear = 1999;
        if (projects && projects.length > 0) {
            const years = projects
                .map(p => p.startDate ? new Date(p.startDate).getFullYear() : null)
                .filter(y => y !== null && !isNaN(y));

            if (years.length > 0) {
                startingYear = Math.min(...years);
            }
        }

        let formattedAreaNum = 0;
        let areaSuffix = "";

        if (totalArea >= 1000000) {
            formattedAreaNum = totalArea / 1000000;
            areaSuffix = "M+";
        } else if (totalArea >= 1000) {
            formattedAreaNum = totalArea / 1000;
            areaSuffix = "K+";
        } else {
            formattedAreaNum = totalArea;
            areaSuffix = "";
        }

        return {
            projects: totalProjects,
            area: parseFloat(formattedAreaNum.toFixed(2)),
            areaSuffix,
            startingYear
        };
    }, [projects]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    const statsVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    return (
        <section ref={containerRef} className="py-20 lg:py-28 xl:py-32 bg-white relative overflow-hidden">
            {/* Decorative Background Text */}
            <div className="absolute top-0 left-0 w-full overflow-hidden pointer-events-none opacity-[0.03]">
                <motion.div style={{ x: useTransform(scrollYProgress, [0, 1], [0, -200]) }} className="whitespace-nowrap text-[10rem] md:text-[20rem] font-bold font-serif leading-none text-primary">
                    VALLETTA
                </motion.div>
            </div>

            <Container className="relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

                    {/* Visual Section (Left) */}
                    <div className="relative order-2 lg:order-1">
                        <div className="relative w-[85%]  md:w-[70%] mx-auto lg:w-full  aspect-[4/5] lg:aspect-[4/5]">
                            {/* Main Image */}
                            <motion.div
                                style={{ y }}
                                className="absolute inset-0 z-10 rounded-sm overflow-hidden shadow-2xl"
                            >
                                <img
                                    src={require("../assets/VALLETA VISTA.webp")}
                                    alt="Valletta Group Building"
                                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-black/20" />
                            </motion.div>

                            {/* Offset Border/Frame */}
                            <motion.div
                                className="absolute -top-6 -left-6 w-full h-full border border-primary/20 z-0"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            />

                            {/* Floating Stats Card */}
                            {/* <motion.div
                                className="absolute -bottom-10 -right-10 z-20 bg-white p-6 md:p-8 shadow-xl max-w-[200px] md:max-w-[280px]"
                                initial={{ y: 50, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                <p className="text-secondary font-serif text-3xl md:text-5xl font-bold mb-1">25+</p>
                                <p className="text-primary text-sm md:text-base font-medium leading-tight">Years of Excellence in Building Dreams</p>
                            </motion.div> */}
                        </div>
                    </div>

                    {/* Content Section (Right) */}
                    <motion.div
                        className="space-y-8 order-1 lg:order-2"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "10%" }}
                    >
                        <motion.div variants={itemVariants}>
                            <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-2 block">About Us</span>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl  font-serif text-primary font-bold leading-tight">
                                <RevealText text="Crafting Landmarks," className='leading-tight' />
                                <span className=" block mt-2 leading-tight">Defining Luxury.</span>
                            </h2>
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <div className="lg:space-y-4 md:space-y-3 space-y-2  lg:text-lg md:text-base text-sm  font-sans leading-relaxed">
                                <ScrollParagraph
                                    value={`Since ${stats.startingYear}, Valletta Group has been a prominent name in Surat's real estate landscape. We don't just build structures; we curate lifestyles. Our commitment to architectural excellence and sustainable living has established us as a symbol of trust and refined living.`}
                                    className="font-medium"
                                />

                                <ScrollParagraph
                                    value={`With over ${stats.area.toFixed(2)}${stats.areaSuffix === "Sq. Ft." ? " " : stats.areaSuffix} Sq. Ft. of constructed space, we seamlessly blend modern innovation with timeless design across our premium residential and commercial projects.`}
                                    className="font-medium"
                                />
                                <ScrollParagraph
                                    value={`As proud members of CREDAI SURAT and SEBA, we uphold the highest standards of quality, transparency, and ethical practices to ensure your peace of mind.`}
                                    className="font-medium"
                                />
                            </div>
                        </motion.div>

                        {/* Logos & Stats Container */}
                        <motion.div
                            variants={itemVariants}
                            className="pt-6 lg:pt-8 grid grid-cols-2 md:grid-cols-[auto_1fr_1fr] lg:grid-cols-2  2xl:grid-cols-[auto_1fr_1fr] gap-y-6 lg:gap-y-8 gap-x-6 lg:gap-x-10 border-t border-gray-200 mt-2"
                        >
                            {/* Membership Logos */}
                            <div className="flex items-center gap-4 lg:gap-6 col-span-2 md:col-span-1 lg:col-span-2 2xl:col-span-1 border-b md:border-b-0 lg:border-b md:border-r lg:border-r-0 2xl:border-b-0 2xl:border-r border-gray-200 pb-6 2xl:pb-0 md:pr-8 lg:pr-0 2xl:pr-10 shrink-0">
                                <img src={require("../assets/credai.png")} alt="CREDAI Surat" className="h-[55px] md:h-16 object-contain hover:-translate-y-1 transition-transform duration-300 border border-secondary/30 rounded-md p-1.5 shadow-sm bg-white" onError={(e) => e.target.style.display = 'none'} />
                                <img src={require("../assets/seba.png")} alt="SEBA Surat" className="h-[55px] md:h-16 object-contain hover:-translate-y-1 transition-transform duration-300 border border-secondary/30 rounded-md p-1.5 shadow-sm bg-white" onError={(e) => e.target.style.display = 'none'} />
                            </div>

                            {/* Stat 1 */}
                            <motion.div variants={statsVariants} className="flex flex-col justify-center border-r border-gray-200 pr-4  lg:pr-8 whitespace-nowrap ">
                                <h3 className="text-3xl md:text-3xl lg:text-4xl font-serif text-primary font-bold mb-1 leading-none">
                                    <AnimatedCounter end={stats.projects || 14} suffix="+" />
                                </h3>
                                <p className="text-secondary/80 font-medium text-sm tracking-wide">Projects</p>
                            </motion.div>

                            {/* Stat 2 */}
                            <motion.div variants={statsVariants} className="flex flex-col justify-center whitespace-nowrap pl-2 md:pl-0">
                                <h3 className="text-3xl md:text-3xl lg:text-4xl font-serif text-primary font-bold mb-1 leading-none">
                                    <AnimatedCounter end={stats.area || 3} suffix={stats.area ? stats.areaSuffix : "M+"} decimals={2} />
                                </h3>
                                <p className="text-secondary/80 font-medium text-sm tracking-wide">Sq. Ft. Delivered</p>
                            </motion.div>
                        </motion.div>

                        <motion.button
                            variants={itemVariants}
                            whileHover={{ x: 10 }}
                            onClick={() => navigate('/about')}
                            className="group flex items-center gap-3 text-secondary font-semibold border-b border-secondary pb-1 hover:text-primary transition-colors mt-4"
                        >
                            READ OUR STORY
                            <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-300" />
                        </motion.button>
                    </motion.div>

                </div>

            </Container>
        </section>
    );
};

export default About;
